import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Image,
  Input,
  SkeletonText,
  StackProps,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { lazy, Suspense, useState } from "react";
import * as yup from "yup";
import { SelectOption } from "../../constants/SelectOption";
import { PremiumStatusInterface } from "../../constants/SupplierProfile";
import useCustomToast from "../../lib/useCustomToast";
import { useBgHover, useRedSoftPrimary } from "../../themes/colors";
import { axiosSupplier } from "../../utils/AxiosInstance";
import { formatDate, formatTime } from "../../utils/helperFunction";
import { CardSkeleton } from "../skeleton/CardSkeleton";
import { ImageInput } from "./ImageInput";
import PricePremiumCard from "../card/PricePremiumCard";

const SelectPublicApi = lazy(() => import("../input/SelectPublicApi"));

interface Props extends StackProps {
  data: PremiumStatusInterface | undefined;
  loaded: boolean;
}

const initialValues = {
  transfer_destination: undefined,
  bank_name: undefined,
  bank_user: undefined,
  file: undefined,
};

export const TabExtendPremium = ({ data, loaded }: Props) => {
  const [bankAccount, setBankAccount] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<SelectOption | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const bgHover = useBgHover();
  const redPrimary = useRedSoftPrimary();
  const toast = useCustomToast();

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: yup.object().shape({
      transfer_destination: yup
        .number()
        .required("Pilihan rekening transfer harus diisi")
        .typeError("Terjadi kesalahan, coba beberapa saat lagi"),
      bank_name: yup
        .string()
        .required("Pilihan rekening yang anda gunakan untuk membayar"),
      bank_user: yup
        .string()
        .required(
          "Masukkan atas nama rekening yang anda gunakan untuk membayar"
        ),
      file: yup
        .mixed()
        .required("Bukti Pembayaran")
        .test("fileType", "File must be an image", (value) => {
          if (!value) return false;
          return (
            value instanceof File &&
            ["image/jpeg", "image/png"].includes(value.type)
          );
        }),
    }),
    onSubmit: (values, { resetForm }) => {
      const formData = new FormData();
      formData.append(
        "transfer_destination",
        values.transfer_destination || ""
      );
      formData.append("bank_name", values.bank_name || "");
      formData.append("bank_user", values.bank_user || "");
      formData.append("file", values.file || "");

      setIsLoading(true);
      axiosSupplier
        .post(`/v1/supplier/premium-buy`, formData)
        .then((res) => {
          toast({
            title: res.data.message,
            status: "success",
          });

          setBankAccount(undefined);
          setSelected(undefined);
          resetForm({ values: initialValues });
        })
        .catch((err) => {
          toast({
            description: err?.response?.data?.message || "",
          });
        })
        .finally(() => setIsLoading(false));
    },
  });

  const handleSelect = (value: any) => {
    if (value.id === selected?.id) {
      setSelected(undefined);
      formik.setFieldValue("transfer_destination", "");
    } else {
      setSelected(value);
      formik.setFieldValue("transfer_destination", value?.id);
    }
  };

  return (
    <>
      <VStack align={"stretch"}>
        <SkeletonText isLoaded={loaded} noOfLines={loaded ? undefined : 1}>
          {data?.expired ? (
            <Text textStyle={"b-reg"}>
              Akun Premium <span style={{ fontWeight: 700 }}>{data?.name}</span>{" "}
              berlaku sampai dengan
            </Text>
          ) : (
            <Text textStyle={"b-reg"} color={"red.400"}>
              Akun premium anda sudah expired
            </Text>
          )}
        </SkeletonText>

        <SkeletonText isLoaded={loaded} noOfLines={loaded ? undefined : 1}>
          {data?.expired && (
            <Text textStyle={"b-reg"}>{`${formatDate(
              `${data?.expired}`
            )} Jam ${formatTime(`${data?.expired}`)}`}</Text>
          )}
        </SkeletonText>
      </VStack>

      {loaded && (
        <>
          <PricePremiumCard />

          {data && data.payments && (
            <VStack w={"full"} mt={4} align={"stretch"}>
              <Text mt={4} textStyle={"h-reg-title"}>
                {data.premiumStatus
                  ? `Tambah durasi akun premium`
                  : `Upgrade akun ke premium`}
              </Text>

              <Text mt={4} textStyle={"b-reg-sb"}>
                Rekening BisnisLink
              </Text>
            </VStack>
          )}

          <form
            id="extendPremiumForm"
            onSubmit={formik.handleSubmit}
            style={{ width: "100%", marginTop: "16px" }}
          >
            <VStack w={"full"} gap={6} align={"stretch"}>
              <FormControl
                isInvalid={
                  formik.errors.file && formik.touched.file ? true : false
                }
              >
                <ImageInput
                  inputName="file"
                  initValue={formik.values.file}
                  isError={
                    formik.touched.file && formik.errors.file ? true : false
                  }
                  onFileChange={async (img) => {
                    formik.setFieldTouched("file", true);
                    await Promise.resolve();
                    formik.setFieldValue("file", img);
                  }}
                  onHandleDrop={async (img) => {
                    formik.setFieldTouched("file", true);
                    await Promise.resolve();
                    formik.setFieldValue("file", img);
                  }}
                />

                <FormErrorMessage>{formik.errors.file}</FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={
                  formik.touched.transfer_destination &&
                  formik.errors.transfer_destination
                    ? true
                    : false
                }
              >
                <FormLabel>Pilih rekening tujuan pembayaran</FormLabel>
                <Wrap gap={8}>
                  {data &&
                    data.payments &&
                    data.payments.map((item, i) => (
                      <WrapItem key={item.id}>
                        <VStack
                          w={"100%"}
                          h={"100%"}
                          p={6}
                          justify={"center"}
                          fontSize={[14, null, 16]}
                          textAlign={"start"}
                          align={"stretch"}
                          borderRadius={["md", null, "lg"]}
                          borderWidth={"1px"}
                          onClick={() => handleSelect(item)}
                          cursor={"pointer"}
                          bg={
                            selected && selected.id === item.id
                              ? redPrimary
                              : undefined
                          }
                          borderColor={
                            selected && selected.id === item.id
                              ? "red.400"
                              : undefined
                          }
                          _hover={{
                            bg:
                              selected && selected.id === item.id
                                ? redPrimary
                                : bgHover,
                          }}
                        >
                          <Image
                            src={
                              item.logo
                                ? `${process.env.REACT_APP_MEDIA_URL}${item.logo}`
                                : ""
                            }
                            fallbackSrc="/assets/svg/illustration_empty.svg"
                            w={"100%"}
                            maxW={"60px"}
                            objectFit={"contain"}
                            objectPosition={"center"}
                          />

                          <Text
                            textStyle={"b-reg-sb"}
                          >{`Rekening ${item.name}`}</Text>

                          <HStack w={"100%"} gap={1}>
                            <Text textStyle={"b-reg"} variant={"secondary"}>
                              Nomor Rekening
                            </Text>

                            <Text textStyle={"b-reg-sb"}>
                              {item.acc_number}
                            </Text>
                          </HStack>

                          <HStack w={"100%"} gap={1}>
                            <Text textStyle={"b-reg"} variant={"secondary"}>
                              a/n
                            </Text>

                            <Text textStyle={"b-reg-sb"}>{item.acc_name}</Text>
                          </HStack>
                        </VStack>
                      </WrapItem>
                    ))}
                </Wrap>

                <FormErrorMessage>
                  {formik.errors.transfer_destination}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={
                  formik.touched.bank_name && formik.errors.bank_name
                    ? true
                    : false
                }
              >
                <FormLabel>Rekening yang anda gunakan untuk membayar</FormLabel>
                <Suspense fallback={<CardSkeleton row={1} height={40} />}>
                  <SelectPublicApi
                    endpoint="/v1/masterdata/bank"
                    inputValue={bankAccount}
                    onSelected={(id) => {
                      setBankAccount(id);
                      formik.setFieldValue("bank_name", id);
                    }}
                  />
                </Suspense>

                <FormErrorMessage>{formik.errors.bank_name}</FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={
                  formik.touched.bank_user && formik.errors.bank_user
                    ? true
                    : false
                }
              >
                <FormLabel>
                  Atas nama rekening yang digunakan untuk pembayaran
                </FormLabel>
                <Input
                  name="bank_user"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.bank_user || ""}
                  autoComplete="off"
                />

                <FormErrorMessage>{formik.errors.bank_user}</FormErrorMessage>
              </FormControl>

              <Button
                w={"fit-content"}
                spinnerPlacement="start"
                isLoading={isLoading}
                loadingText="Loading"
                colorScheme="red"
                type="submit"
              >
                Bayar Sekarang
              </Button>
            </VStack>
          </form>
        </>
      )}
    </>
  );
};
